import React, { useState } from 'react';
import { 
  DocumentTextIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useReleaseNotes, useGenerateReleaseNotes } from '../hooks/useReleaseNotes';
import type { NodeReleaseNotes, ReleaseNotesBaselineType } from '../types/releaseNotes';

interface ReleaseNotesSectionProps {
  releaseId: string;
}

const baselineTypeLabel: Record<ReleaseNotesBaselineType, string> = {
  ProductionDeployment: 'Last production deployment',
  StagingDeployment: 'Last staging deployment (no prod found)',
  OldestAvailable: 'Oldest available build (no prod/staging found)',
  NoPreviousBuild: 'No previous build found',
};

const baselineTypeColor: Record<ReleaseNotesBaselineType, string> = {
  ProductionDeployment: 'text-green-700 bg-green-50',
  StagingDeployment: 'text-blue-700 bg-blue-50',
  OldestAvailable: 'text-amber-700 bg-amber-50',
  NoPreviousBuild: 'text-gray-600 bg-gray-50',
};

export const ReleaseNotesSection: React.FC<ReleaseNotesSectionProps> = ({ releaseId }) => {
  const { data: releaseNotes, isLoading, error } = useReleaseNotes(releaseId);
  const generateMutation = useGenerateReleaseNotes();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleGenerate = () => {
    generateMutation.mutate(releaseId);
  };

  return (
    <div className="mt-8 bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5" />
            Release Notes
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Changes between last production deployment and selected pipeline runs.
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generateMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowPathIcon className={`h-4 w-4 ${generateMutation.isPending ? 'animate-spin' : ''}`} />
          {generateMutation.isPending ? 'Generating...' : releaseNotes ? 'Regenerate' : 'Generate'}
        </button>
      </div>

      <div className="px-6 py-4">
        {isLoading && (
          <div className="text-center py-8 text-gray-500">
            Loading release notes...
          </div>
        )}

        {error && !isLoading && (
          <div className="text-center py-8 text-red-600">
            Failed to load release notes.
          </div>
        )}

        {generateMutation.isError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            Failed to generate release notes. Please try again.
          </div>
        )}

        {!isLoading && !error && !releaseNotes && (
          <div className="text-center py-8 text-gray-500">
            <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No release notes generated yet.</p>
            <p className="mt-1 text-sm">Click "Generate" to create release notes based on selected pipeline runs.</p>
          </div>
        )}

        {releaseNotes?.overallMessage && (
          <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm flex items-center gap-2">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
            {releaseNotes.overallMessage}
          </div>
        )}

        {releaseNotes && releaseNotes.nodes.length > 0 && (
          <div className="space-y-4">
            {releaseNotes.generatedAt && (
              <p className="text-xs text-gray-400">
                Generated {new Date(releaseNotes.generatedAt).toLocaleString()}
              </p>
            )}
            {releaseNotes.nodes.map(node => (
              <NodeReleaseNotesCard
                key={node.releaseFlowNodeId}
                node={node}
                isExpanded={expandedNodes.has(node.releaseFlowNodeId)}
                onToggle={() => toggleNode(node.releaseFlowNodeId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface NodeReleaseNotesCardProps {
  node: NodeReleaseNotes;
  isExpanded: boolean;
  onToggle: () => void;
}

const NodeReleaseNotesCard: React.FC<NodeReleaseNotesCardProps> = ({ node, isExpanded, onToggle }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div>
            <span className="font-medium text-gray-900">{node.pipelineName}</span>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${baselineTypeColor[node.baselineType]}`}>
            {baselineTypeLabel[node.baselineType]}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">
            {node.pullRequests.length} PR{node.pullRequests.length !== 1 ? 's' : ''}
            {' / '}
            {node.commits.length} commit{node.commits.length !== 1 ? 's' : ''}
          </div>
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3">
          {/* Build range info */}
          <div className="mb-3 text-sm text-gray-600">
            <span className="font-medium">Build:</span>{' '}
            {node.baselineBuildNumber ? (
              <>#{node.baselineBuildNumber} → #{node.selectedBuildNumber}</>
            ) : (
              <>#{node.selectedBuildNumber} (no baseline)</>
            )}
          </div>

          {node.baselineMessage && (
            <div className="mb-3 p-2 bg-amber-50 text-amber-700 rounded text-sm flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
              {node.baselineMessage}
            </div>
          )}

          {/* Pull Requests */}
          {node.pullRequests.length > 0 ? (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Pull Requests</h4>
              <div className="space-y-2">
                {node.pullRequests.map(pr => (
                  <div key={pr.id} className="p-3 bg-gray-50 rounded-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <a
                          href={pr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          #{pr.id} {pr.title}
                        </a>
                        {pr.description && (
                          <p className="mt-1 text-xs text-gray-500 line-clamp-2">{pr.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                      <span>{pr.author}</span>
                      <span>{pr.sourceBranch} → {pr.targetBranch}</span>
                      <span>{new Date(pr.mergedDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="mb-4 text-sm text-gray-500">No pull requests found for this range.</p>
          )}

          {/* Commits (collapsed by default) */}
          {node.commits.length > 0 && (
            <CommitsList commits={node.commits} />
          )}
        </div>
      )}
    </div>
  );
};

interface CommitsListProps {
  commits: NodeReleaseNotes['commits'];
}

const CommitsList: React.FC<CommitsListProps> = ({ commits }) => {
  const [showCommits, setShowCommits] = useState(false);

  return (
    <div>
      <button
        onClick={() => setShowCommits(!showCommits)}
        className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
      >
        {showCommits ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
        {showCommits ? 'Hide' : 'Show'} {commits.length} commit{commits.length !== 1 ? 's' : ''}
      </button>
      {showCommits && (
        <div className="mt-2 space-y-1">
          {commits.map(commit => (
            <div key={commit.id} className="flex items-start gap-2 text-xs py-1">
              <code className="text-gray-400 font-mono flex-shrink-0">{commit.id.substring(0, 7)}</code>
              <span className="text-gray-700 truncate">{commit.message}</span>
              <span className="text-gray-400 flex-shrink-0">{commit.author}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReleaseNotesSection;
